// Static stereogram pipeline: source -> depth -> post-processing -> stereogram.

import { buildDepthOps } from './depth/ops';
import { loadFont } from './depth/fonts';
import { renderTextDepth } from './depth/text3d';
import { fitSize } from './image';
import { estimateDepth } from './ml/client.svelte';
import { form, runJob } from './server/api';
import { initGeometry } from './scene/geometry';
import { renderSceneDepth } from './scene/render';
import { sceneStore } from './scene/store.svelte';
import { computeTarget } from './server/compute.svelte';
import { endJob, trackJob } from './server/jobs.svelte';
import type { StudioSettings } from './state.svelte';
import type { DepthData, RgbaData, TextureSpec } from './types';
import type { WorkerPool } from './wasm/pool';

export interface Inputs {
  sourceImage: RgbaData | null;
  sourceFile?: File | null;
  uploadedDepth: DepthData | null;
  textureImage: RgbaData | null;
}

/** Output size for the current source. Image/depth sources keep their aspect ratio. */
export function outputSize(s: StudioSettings, inputs: Inputs): [number, number] {
  const src = s.sourceKind === 'image' ? inputs.sourceImage : s.sourceKind === 'depth' ? inputs.uploadedDepth : null;
  if (src) return fitSize(src.width, src.height, s.output.maxSide, s.output.maxSide, true);
  return [Math.max(16, Math.round(s.output.width)), Math.max(16, Math.round(s.output.height))];
}

/** Raw (unprocessed) depth for the selected source, or null if it is missing. */
export async function sourceDepth(
  pool: WorkerPool,
  s: StudioSettings,
  inputs: Inputs,
  width: number,
  height: number,
): Promise<DepthData | null> {
  switch (s.sourceKind) {
    case 'text': {
      const font = await loadFont(s.text.font);
      return renderTextDepth(font, s.text, width, height);
    }
    case 'image':
      if (!inputs.sourceImage) return null;
      if (s.heuristic === 'ml') {
        if (computeTarget() === 'server') return serverDepth(pool, s.serverModel, inputs);
        return estimateDepth(inputs.sourceImage);
      }
      return pool.call('depthFromImage', {
        image: inputs.sourceImage,
        heuristic: s.heuristic === 'edges' ? { kind: 'edges', threshold: s.edgeThreshold } : { kind: 'brightness' },
      });
    case 'depth':
      return inputs.uploadedDepth;
    case 'scene':
      await initGeometry();
      return renderSceneDepth(sceneStore.current, sceneStore.time, width, height);
  }
}

/** Depth estimation on the home server (larger models, GPU). */
async function serverDepth(pool: WorkerPool, model: string, inputs: Inputs): Promise<DepthData> {
  let file: Blob | undefined = inputs.sourceFile ?? undefined;
  if (!file && inputs.sourceImage) {
    const png = await pool.call('encodePng', { image: inputs.sourceImage });
    file = new File([png as Uint8Array<ArrayBuffer>], 'image.png', { type: 'image/png' });
  }
  try {
    const { blob } = await runJob('depth', form({ model }, { image: file }), trackJob('depth'));
    return pool.call('decodeDepthPng', { bytes: new Uint8Array(await blob.arrayBuffer()) });
  } finally {
    endJob();
  }
}

export function processDepth(pool: WorkerPool, s: StudioSettings, depth: DepthData, width: number, height: number) {
  return pool.call('processDepth', { depth, ops: buildDepthOps(s.depth, width, height) });
}

/** Pixels a texture spec needs, if any. */
export function texturePixels(spec: TextureSpec, inputs: Inputs): RgbaData | undefined {
  if (spec.kind === 'image') return inputs.textureImage ?? undefined;
  if (spec.kind === 'source') return inputs.sourceImage ?? undefined;
  return undefined;
}

export function renderStereogram(pool: WorkerPool, s: StudioSettings, depth: DepthData, inputs: Inputs) {
  return pool.call('render', {
    depth,
    params: s.stereo,
    texture: s.texture,
    textureImage: texturePixels(s.texture, inputs),
  });
}
